import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, DoorOpen, Library, Users, AlertTriangle, Download, BookOpen, TrendingUp } from "lucide-react";

const dayLabel = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });

const Bars = ({ series, keys, colors, labels }) => {
  const max = Math.max(1, ...series.flatMap(s => keys.map(k => s[k] || 0)));
  return (
    <div>
      <div className="flex items-end gap-2 h-28">
        {series.map(s => (
          <div key={s.date} className="flex-1 flex flex-col items-center gap-0.5">
            <div className="w-full flex items-end justify-center gap-0.5 h-24">
              {keys.map((k, i) => (
                <div key={k} title={`${labels[i]}: ${s[k] || 0}`} className={`w-2.5 rounded-t ${colors[i]}`}
                  style={{ height: `${((s[k] || 0) / max) * 100}%`, minHeight: (s[k] || 0) > 0 ? 3 : 0 }} />
              ))}
            </div>
            <span className="text-[10px] text-slate-400">{dayLabel(s.date)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-2">
        {labels.map((l, i) => (
          <span key={l} className="text-xs text-slate-500 flex items-center gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded ${colors[i]}`} />{l}
          </span>
        ))}
      </div>
    </div>
  );
};

const MiniKPI = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
      <Icon className={`h-4 w-4 ${color}`} />
    </div>
    <div className={`text-xl font-extrabold ${color} tabular-nums`}>{value}</div>
  </div>
);

export const BIPresenceLibrary = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/bi/presence-library").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const { presence, gate, library } = d;
  return (
    <>
      <div data-testid="bi-presence-section">
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><Clock className="h-5 w-5" />Présence personnel & séances</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniKPI label="Pointages aujourd'hui" value={presence.staff_pointages_today} icon={Clock} color="text-[#1E3A8A]" />
          <MiniKPI label="Présents maintenant" value={presence.staff_in_now} icon={Users} color="text-[#166534]" />
          <MiniKPI label="Heures cumulées (jour)" value={`${presence.hours_today} h`} icon={TrendingUp} color="text-[#EA580C]" />
          <MiniKPI label="Séances (jour / semaine)" value={`${presence.sessions_today} / ${presence.sessions_week}`} icon={BookOpen} color="text-purple-600" />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
          <div className="font-heading font-bold text-sm text-[#1E3A8A] mb-3">Pointages personnel — 7 derniers jours</div>
          <Bars series={presence.staff_series} keys={["pointages", "employes"]} colors={["bg-[#1E3A8A]", "bg-[#166534]"]} labels={["Pointages", "Employés distincts"]} />
        </div>
      </div>

      <div data-testid="bi-gate-section">
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><DoorOpen className="h-5 w-5" />Portail élèves — entrées / sorties</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniKPI label="Entrées aujourd'hui" value={gate.entries_today} icon={DoorOpen} color="text-[#166534]" />
          <MiniKPI label="Sorties aujourd'hui" value={gate.exits_today} icon={DoorOpen} color="text-[#EA580C]" />
          <MiniKPI label="Présents dans l'école" value={gate.students_present} icon={Users} color="text-[#1E3A8A]" />
          <MiniKPI label="Alertes sorties" value={gate.alerts_today} icon={AlertTriangle} color={gate.alerts_today > 0 ? "text-red-600" : "text-slate-400"} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
          <div className="font-heading font-bold text-sm text-[#1E3A8A] mb-3">Mouvements — 7 derniers jours</div>
          <Bars series={gate.series} keys={["entrees", "sorties"]} colors={["bg-[#166534]", "bg-[#EA580C]"]} labels={["Entrées", "Sorties"]} />
        </div>
      </div>

      <div data-testid="bi-library-section">
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] mb-3 flex items-center gap-2"><Library className="h-5 w-5" />Bibliothèque numérique</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniKPI label="Documents" value={library.items_total} icon={Library} color="text-[#1E3A8A]" />
          <MiniKPI label="Lectures / téléch." value={library.downloads_total} icon={Download} color="text-[#166534]" />
          <MiniKPI label="Emprunts en cours" value={library.loans_active} icon={BookOpen} color="text-[#EA580C]" />
          <MiniKPI label="Emprunts en retard" value={library.loans_overdue} icon={AlertTriangle} color={library.loans_overdue > 0 ? "text-red-600" : "text-slate-400"} />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-heading font-bold text-sm text-[#1E3A8A] mb-3">Top 5 des documents les plus lus</div>
            {library.top_items.filter(t => t.downloads > 0).length === 0 ? (
              <div className="text-sm text-slate-400">Pas encore de lectures — encouragez élèves et enseignants !</div>
            ) : library.top_items.map((t, i) => (
              <div key={t.title} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                <span className="truncate pr-2"><span className="font-bold text-[#EA580C]">{i + 1}.</span> {t.title}</span>
                <span className="tabular-nums font-semibold text-[#1E3A8A] shrink-0">{t.downloads}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-heading font-bold text-sm text-[#1E3A8A] mb-3">Catalogue par catégorie</div>
            {library.by_category.map(c => {
              const pct = library.items_total > 0 ? (c.count / library.items_total) * 100 : 0;
              return (
                <div key={c.category} className="mb-2">
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="font-semibold">{c.category}</span>
                    <span className="tabular-nums text-slate-500">{c.count} doc · {c.downloads} lect.</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#166534]" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
